import type {
  OfferDocumentDraft,
  OfferDocumentEditablePara,
  OfferVatMode,
} from '../../../types';
import {
  DEFAULT_SKONTO_DAYS,
  formatPaymentTermsSentence,
  normalizePaymentTermsDays,
  normalizePaymentTermsMode,
  paymentTermsContextFromDocumentType,
  type PaymentTermsContext,
  type PaymentTermsMode,
} from '../../../utils/paymentTerms';
import { parseLaborCostShareMode, type LaborCostShareMode } from './offerLaborCostShare';
import { isOfferVatMode } from './offerVat';
import {
  normalizeOfferDocumentContent,
  replaceTextBlocksKeepingMedia,
  updateFirstTextBlock,
} from './offerDocumentContent';

export type OfferPaymentTermsInput = {
  mode?: PaymentTermsMode | unknown;
  days?: unknown;
  skontoPercent?: unknown;
  skontoDays?: unknown;
};

export type OfferDocumentPaymentTerms = NonNullable<OfferDocumentDraft['paymentTerms']>;

export function normalizeSkontoPercent(raw: unknown): number | null {
  if (raw == null || raw === '') return null;
  const n = typeof raw === 'number' ? raw : parseFloat(String(raw).replace(',', '.'));
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.min(100, Math.round(n * 100) / 100);
}

export function normalizeSkontoDays(raw: unknown): number | null {
  if (raw == null || raw === '') return null;
  const n = typeof raw === 'number' ? raw : parseInt(String(raw).trim(), 10);
  if (!Number.isFinite(n) || n < 1) return null;
  return Math.min(9999, Math.floor(n));
}

export function parseOfferDocumentPaymentTerms(raw: unknown): OfferDocumentPaymentTerms | undefined {
  if (typeof raw !== 'object' || raw == null || Array.isArray(raw)) return undefined;
  const o = raw as Record<string, unknown>;
  const mode = normalizePaymentTermsMode(o.mode);
  const days = mode === 'net_days' ? normalizePaymentTermsDays(o.days) : undefined;
  const skontoPercent = mode === 'immediate' ? null : normalizeSkontoPercent(o.skontoPercent);
  const skontoDays =
    mode === 'immediate' || skontoPercent == null
      ? null
      : normalizeSkontoDays(o.skontoDays) ?? DEFAULT_SKONTO_DAYS;
  return {
    mode,
    ...(days != null ? { days } : {}),
    skontoPercent,
    skontoDays,
  };
}

export function paymentTermsFromSettings(opts: {
  mode?: unknown;
  days?: unknown;
  skontoPercent?: unknown;
  skontoDays?: unknown;
}): OfferDocumentPaymentTerms {
  return (
    parseOfferDocumentPaymentTerms({
      mode: opts.mode,
      days: opts.days,
      skontoPercent: opts.skontoPercent,
      skontoDays: opts.skontoDays,
    }) ?? { mode: 'net_days', days: 7, skontoPercent: null, skontoDays: null }
  );
}

export type OfferDocumentMetaPatch = {
  offerTitleRest?: string;
  leistungsort?: string;
  leistungszeitraum?: OfferDocumentDraft['leistungszeitraum'] | null;
  lieferdatum?: OfferDocumentDraft['lieferdatum'] | null;
  issueDate?: string;
  recipientNameLines?: string[];
  recipientStreetLine?: string;
  recipientPlzOrtLine?: string;
  vat_mode?: OfferVatMode;
  discount?: OfferDocumentDraft['discount'] | null;
  payment_terms?: OfferPaymentTermsInput | null;
  introLetterParas?: string[];
  closingGreeting?: string;
  hideTotals?: boolean;
  hideArticleNumber?: boolean;
  hidePositionDescriptions?: boolean;
  positionDetailLevel?: 'full' | 'titles';
  calculationHourlyRate?: number;
  calculationLaborSeparately?: boolean;
  laborCostShare?: LaborCostShareMode;
};

function newParaId(): string {
  return `b_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

function parasFromTexts(texts: string[]): OfferDocumentEditablePara[] {
  return texts.map((text) => ({ id: newParaId(), text }));
}

export function paymentTermsContextFromDraft(
  draft: OfferDocumentDraft,
  fallbackLabel = 'Angebot',
): PaymentTermsContext {
  return paymentTermsContextFromDocumentType(draft.documentType || fallbackLabel);
}

export function applyPaymentTermsToOfferDraft(
  draft: OfferDocumentDraft,
  terms: OfferPaymentTermsInput,
  context?: PaymentTermsContext,
): OfferDocumentDraft {
  const sentence = formatPaymentTermsSentence({
    mode: normalizePaymentTermsMode(terms.mode),
    days: normalizePaymentTermsDays(terms.days),
    context: context ?? paymentTermsContextFromDraft(draft),
    skontoPercent: terms.skontoPercent,
    skontoDays: terms.skontoDays,
  });
  const existing = Array.isArray(draft.afterTotalsParas) ? draft.afterTotalsParas : [];
  const nextParas =
    existing.length > 0
      ? [{ ...existing[0], text: sentence }, ...existing.slice(1)]
      : parasFromTexts([sentence]);
  const closingContent = updateFirstTextBlock(
    normalizeOfferDocumentContent({
      content: draft.closingContent,
      paras: existing.length > 0 ? existing : nextParas,
      blocks: draft.closingBlocks,
    }),
    sentence,
  );
  const structured = parseOfferDocumentPaymentTerms({
    mode: terms.mode,
    days: terms.days,
    skontoPercent: terms.skontoPercent,
    skontoDays: terms.skontoDays,
  });
  return {
    ...draft,
    afterTotalsParas: nextParas,
    closingContent,
    paymentTermsCustomized: true,
    ...(structured ? { paymentTerms: structured } : {}),
  };
}

export function applyOfferDocumentMetaPatch(
  draft: OfferDocumentDraft,
  patch: OfferDocumentMetaPatch,
): OfferDocumentDraft {
  let next: OfferDocumentDraft = { ...draft };

  if (patch.offerTitleRest !== undefined) next.offerTitleRest = String(patch.offerTitleRest ?? '').trim();
  if (patch.leistungsort !== undefined) next.leistungsort = String(patch.leistungsort ?? '').trim();
  if (patch.leistungszeitraum !== undefined) {
    if (patch.leistungszeitraum == null) delete next.leistungszeitraum;
    else next.leistungszeitraum = patch.leistungszeitraum;
  }
  if (patch.lieferdatum !== undefined) {
    if (patch.lieferdatum == null) delete next.lieferdatum;
    else next.lieferdatum = patch.lieferdatum;
  }
  if (patch.issueDate !== undefined) {
    const issueDate = String(patch.issueDate ?? '').trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(issueDate)) next.issueDate = issueDate;
  }
  if (patch.recipientNameLines !== undefined) {
    next.recipientNameLines = patch.recipientNameLines
      .map((line) => String(line ?? '').trim())
      .filter(Boolean)
      .slice(0, 8);
  }
  if (patch.recipientStreetLine !== undefined) {
    next.recipientStreetLine = String(patch.recipientStreetLine ?? '').trim();
  }
  if (patch.recipientPlzOrtLine !== undefined) {
    next.recipientPlzOrtLine = String(patch.recipientPlzOrtLine ?? '').trim();
  }
  if (patch.vat_mode !== undefined && isOfferVatMode(patch.vat_mode)) {
    next.vat_mode = patch.vat_mode;
  }
  if (patch.discount !== undefined) {
    if (patch.discount == null) delete next.discount;
    else next.discount = patch.discount;
  }
  if (patch.payment_terms !== undefined) {
    if (patch.payment_terms == null) {
      delete next.paymentTermsCustomized;
      delete next.paymentTerms;
    } else {
      next = applyPaymentTermsToOfferDraft(next, patch.payment_terms);
    }
  }
  if (patch.introLetterParas !== undefined) {
    const texts = patch.introLetterParas.map((text) => String(text ?? ''));
    next.introLetterParas = parasFromTexts(texts);
    next.introContent = replaceTextBlocksKeepingMedia(
      normalizeOfferDocumentContent({
        content: next.introContent,
        paras: next.introLetterParas,
        blocks: next.introBlocks,
      }),
      texts,
    );
  }
  if (patch.closingGreeting !== undefined) {
    next.closingGreeting = String(patch.closingGreeting ?? '');
  }
  if (typeof patch.hideTotals === 'boolean') next.hideTotals = patch.hideTotals;
  if (typeof patch.hideArticleNumber === 'boolean') next.hideArticleNumber = patch.hideArticleNumber;
  if (typeof patch.hidePositionDescriptions === 'boolean') {
    next.hidePositionDescriptions = patch.hidePositionDescriptions;
  }
  if (patch.positionDetailLevel === 'full' || patch.positionDetailLevel === 'titles') {
    next.positionDetailLevel = patch.positionDetailLevel;
  }
  if (typeof patch.calculationHourlyRate === 'number' && Number.isFinite(patch.calculationHourlyRate)) {
    next.calculationHourlyRate = Math.max(0, patch.calculationHourlyRate);
  }
  if (typeof patch.calculationLaborSeparately === 'boolean') {
    next.calculationLaborSeparately = patch.calculationLaborSeparately;
  }
  if (patch.laborCostShare !== undefined) {
    next.laborCostShare = parseLaborCostShareMode(patch.laborCostShare);
  }

  return next;
}
